import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import noop from 'lodash/noop';
import uniq from 'lodash/uniq';

import { AI_TOOL_ACTIVATED } from '../constants';
import { AIChatFile, LailaMessage, User } from '../../../types';
import { LailaChatEvent } from '../../../api/ai/sse';
import {
  speechToTextRequest,
  textToSpeechRequest,
  createVectorStoreRequest,
  deleteVectorStoreRequest,
  LailaFaq,
} from '../../../api/ai';
import { saveToLS } from '../../../utils/local-storage';
import { getPersonalizedMessage } from '../utils';
import { NotificationContext } from '../../../contexts/notificationContext';
import { useLailaChat } from '../hooks/useLailaChat';
import { LailaCardsContext } from '../components/cards/LailaCardsContext';
import { blobToBase64 } from '../../../utils/blobToBase64';

interface Data {
  messages: LailaMessage[];
  // Until Laila's answer is complete; it shows in the thread as it is written.
  thinking: boolean;
  // What Laila is doing before her first words, e.g. "Checking who's clocked in".
  status: string;
  // The unsent text; it survives leaving the chat page.
  draft: string;
  setDraft: (draft: string) => void;
  send: (text: string) => void;
  retry: () => void;
  stop: () => void;
  isAIActivated: boolean;
  setIsAIActivated: (status: boolean) => void;
  activateAIChat: () => void;
  speechToText: (audioFile: string) => Promise<string>;
  // Laila's voice: an MP3, base64-encoded, or '' when it failed.
  textToSpeech: (text: string) => Promise<string>;
  // In voice mode Laila answers in a few short spoken sentences.
  voice: boolean;
  setVoice: (voice: boolean) => void;
  // Voice mode hears each spoken sentence of an answer here, as it arrives.
  setAudioListener: (listener: LailaAudioListener | null) => void;
  // Attached to the next message; every upload of the conversation stays searchable.
  files: AIChatFile[];
  setFiles: (files: AIChatFile[]) => void;
  uploadFile: (file: File) => Promise<void>;
  deleteFile: (fileName: string) => Promise<void>;
  user?: User;
}

const initialData: Data = {
  messages: [],
  thinking: false,
  status: '',
  draft: '',
  setDraft: noop,
  send: noop,
  retry: noop,
  stop: noop,
  isAIActivated: false,
  setIsAIActivated: noop,
  activateAIChat: noop,
  speechToText: () => Promise.resolve(''),
  textToSpeech: () => Promise.resolve(''),
  voice: false,
  setVoice: noop,
  setAudioListener: noop,
  files: [],
  setFiles: noop,
  uploadFile: () => Promise.resolve(),
  deleteFile: () => Promise.resolve(),
  user: undefined,
};

export type LailaAudio = Extract<LailaChatEvent, { type: 'audio' }>;
export type LailaAudioListener = (audio: LailaAudio) => void;

export const AIChatContext = createContext(initialData);

// Laila is a woman's name; OpenAI's "nova" is the voice that fits.
const LAILA_VOICE = 'nova';

const VOICE_INSTRUCTIONS =
  'The user is talking to you by voice and will hear your answer read aloud. Answer in one to three short ' +
  'spoken sentences of plain text: no markdown, lists, tables, links or emoji. Say numbers the way people ' +
  'say them. If there is more to tell, give the headline and offer the detail.';

// Files travel base64-encoded through a Lambda, whose request limit is 6 MB.
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

interface Props {
  children: any;
  user?: User;
  model?: string;
  companyId?: string;
  // Always searched, on top of the files the user uploads (e.g. the FAQ stores).
  vectorStoreIds?: string[];
  // The help-centre FAQ Laila knows: employers' or workers'.
  faq?: LailaFaq;
}

function AIChatContextProvider({ children, user, model, companyId, vectorStoreIds = [], faq }: Props) {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const [draft, setDraft] = useState('');
  const [isAIActivated, setIsAIActivated] = useState<boolean>(false);
  const [files, setFiles] = useState<AIChatFile[]>([]);
  const [uploads, setUploads] = useState<AIChatFile[]>([]);
  const [voice, setVoice] = useState(false);
  const audioListener = useRef<LailaAudioListener | null>(null);
  const setAudioListener = useCallback((listener: LailaAudioListener | null) => {
    audioListener.current = listener;
  }, []);

  const instructions = useMemo(() => {
    const personal = user ? getPersonalizedMessage(user) : undefined;
    return voice ? [personal, VOICE_INSTRUCTIONS].filter(Boolean).join(' ') : personal;
  }, [user, voice]);
  const chat = useLailaChat({
    instructions,
    model,
    vectorStoreIds: [...uniq(uploads.map(file => file.vectorStoreId)), ...vectorStoreIds],
    faq,
    // The time tools answer employers only; offering them to a worker would
    // just cost a round trip that ends in "not allowed".
    companyId: user?.isCompanyAdmin ? companyId : undefined,
    voice: voice ? LAILA_VOICE : undefined,
    onAudio: audio => audioListener.current?.(audio),
  });

  // The attached files and the draft go with the question.
  const send = (text: string) => {
    if (chat.send(text, files)) {
      setFiles([]);
      setDraft('');
    }
  };

  // The chat's one store for its uploads: OpenAI searches at most 2 stores per
  // question. Uploads queue up, so two at once don't each start a store.
  const uploadStore = useRef<string>();
  const uploadQueue = useRef<Promise<void>>(Promise.resolve());

  const uploadFile = (file: File) => {
    uploadQueue.current = uploadQueue.current.then(() => addUpload(file));
    return uploadQueue.current;
  };

  const addUpload = async (file: File) => {
    if (file.size > MAX_UPLOAD_BYTES) {
      showNotification({
        message: `${file.name} is too big. Laila reads files up to ${MAX_UPLOAD_BYTES / 1024 / 1024} MB.`,
        options: { variant: 'warning' },
      });
      return;
    }
    try {
      const base64File = await blobToBase64(new Blob([file], { type: file.type }));
      const res = await createVectorStoreRequest(base64File, file.name, uploadStore.current);
      const { fileId, vectorStoreId } = res.data.body;
      uploadStore.current = vectorStoreId;
      const upload = { fileId, vectorStoreId, name: file.name, size: file.size };
      setFiles(value => [...value, upload]);
      setUploads(value => [...value, upload]);
    } catch (e) {
      handleServerError(e);
    }
  };

  // Removes a file attached to the next message: it was never asked about.
  const deleteFile = async (fileName: string) => {
    const file = files.find(f => f.name === fileName);
    if (!file) {
      return;
    }
    try {
      await deleteVectorStoreRequest(file.fileId, file.vectorStoreId);
      setFiles(value => value.filter(f => f.fileId !== file.fileId));
      setUploads(value => value.filter(f => f.fileId !== file.fileId));
      // The store goes with its last file; the next upload starts a new one.
      if (uploads.every(f => f.fileId === file.fileId)) {
        uploadStore.current = undefined;
      }
    } catch (e) {
      handleServerError(e);
    }
  };

  const speechToText = async (audioFile: string) => {
    try {
      const res = await speechToTextRequest(audioFile);
      return res.data?.statusCode === 200 ? res.data.body : '';
    } catch (e) {
      handleServerError(e);
      return '';
    }
  };

  const textToSpeech = async (text: string) => {
    try {
      const res = await textToSpeechRequest(text, LAILA_VOICE);
      return res.data?.statusCode === 200 ? res.data.body : '';
    } catch (e) {
      return '';
    }
  };

  const activateAIChat = () => {
    setIsAIActivated(true);
    saveToLS(AI_TOOL_ACTIVATED, 'true');
  };

  // The provider outlives the chat page, so uploads are removed when it goes:
  // on logout or a company switch. The ref keeps the cleanup from seeing only
  // the uploads of the first render.
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;
  useEffect(() => {
    return () => {
      uploadsRef.current.forEach(file =>
        deleteVectorStoreRequest(file.fileId, file.vectorStoreId).catch(noop)
      );
    };
  }, []);

  const { updateCard, noteForLaila } = chat;
  const chatSend = chat.send;
  // A tap on a card's answer (a site, a shift) is the user's next message.
  const cardActions = useMemo(
    () => ({ updateCard, noteForLaila, answer: (text: string) => void chatSend(text) }),
    [updateCard, noteForLaila, chatSend]
  );

  const value: Data = {
    messages: chat.messages,
    thinking: chat.thinking,
    status: chat.status,
    draft,
    setDraft,
    send,
    retry: chat.retry,
    stop: chat.stop,
    isAIActivated,
    setIsAIActivated,
    activateAIChat,
    speechToText,
    textToSpeech,
    voice,
    setVoice,
    setAudioListener,
    files,
    setFiles,
    uploadFile,
    deleteFile,
    user,
  };

  return (
    <AIChatContext.Provider value={value}>
      <LailaCardsContext.Provider value={cardActions}>{children}</LailaCardsContext.Provider>
    </AIChatContext.Provider>
  );
}

export default AIChatContextProvider;
